// App.tsx
import React, { useState, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  Layout,
  Button,
  Input,
  Typography,
  message,
  Card,
  Menu,
  Radio,
  ConfigProvider,
  Drawer, // --- NEW IMPORT
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  AppstoreOutlined,
  BarsOutlined,
  HomeOutlined,
  MenuOutlined,
  TeamOutlined,
  SolutionOutlined,
} from "@ant-design/icons";
import "./App.css";

import { Candidate, Status, Role } from "./types";
import { CandidateService } from "./services/candidateService";
import { RoleService } from "./services/roleService";
import { CandidateTable } from "./components/CandidateTable";
import { AddCandidateModal } from "./components/AddCandidateModal";
import { KanbanBoard } from "./components/KanbanBoard";
import { CandidateDrawer } from "./components/CandidateDrawer";
import { RoleManager } from "./components/RoleManager";
import { Home } from "./components/Home";

const { Header, Content, Footer } = Layout;
const { Title, Text } = Typography;

const AppLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // --- Global Data State ---
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);

  // --- UI State ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // --- NEW STATE
  const [searchText, setSearchText] = useState("");
  const [viewMode, setViewMode] = useState<"table" | "board">("table");
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(
    null
  );

  useEffect(() => {
    refreshData();
  }, []); // eslint-disable-line

  const refreshData = () => {
    const allCandidates = CandidateService.getAll();
    setCandidates(allCandidates);
    const allRoles = RoleService.getAll();
    setRoles(allRoles);

    if (selectedCandidate) {
      const updated = allCandidates.find((c) => c.id === selectedCandidate.id);
      if (updated) setSelectedCandidate(updated);
    }
  };

  // --- HANDLERS ---
  const handleAddCandidate = (values: any) => {
    CandidateService.add(values);
    message.success("Candidate added successfully");
    setIsModalOpen(false);
    refreshData();
  };

  const handleDeleteCandidate = (id: string) => {
    CandidateService.delete(id);
    message.success("Candidate removed");
    refreshData();
  };

  const handleStatusChange = (id: string, newStatus: Status) => {
    CandidateService.updateStatus(id, newStatus);
    refreshData();
  };

  const handleAddNote = (id: string, text: string) => {
    CandidateService.addNote(id, text);
    message.success("Note added");
    refreshData();
  };

  const handleAddRole = (roleData: Omit<Role, "id">) => {
    RoleService.add(roleData);
    refreshData();
  };

  const handleDeleteRole = (id: string) => {
    RoleService.delete(id);
    message.success("Role deleted");
    refreshData();
  };

  const handleReorderRoles = (newRoles: Role[]) => {
    setRoles(newRoles);
    RoleService.saveAll(newRoles);
  };

  // --- Render Helpers ---
  const filteredCandidates = candidates.filter(
    (c) =>
      c.name.toLowerCase().includes(searchText.toLowerCase()) ||
      c.role.toLowerCase().includes(searchText.toLowerCase())
  );

  const CandidateView = () => (
    <>
      <div
        style={{
          marginBottom: 24,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
        }}
      >
        <div>
          <Title level={2} style={{ margin: 0 }}>
            Candidates
          </Title>
          <Text type="secondary">Manage your recruitment pipeline</Text>
        </div>

        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <Input
            placeholder="Search..."
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            style={{ width: 200, borderRadius: 8 }}
            size="large"
            onChange={(e) => setSearchText(e.target.value)}
            value={searchText}
            allowClear
          />
          <div className="desktop-menu" style={{ display: "flex", gap: 16 }}>
            <Radio.Group
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              buttonStyle="solid"
              size="large"
            >
              <Radio.Button value="table">
                <BarsOutlined />
              </Radio.Button>
              <Radio.Button value="board">
                <AppstoreOutlined />
              </Radio.Button>
            </Radio.Group>
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={() => setIsModalOpen(true)}
              style={{ borderRadius: 8, fontWeight: 600 }}
            >
              Add Candidate
            </Button>
          </div>
          {/* Mobile Only Add Button (Icon only to save space) */}
          <Button
            className="mobile-menu-btn"
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={() => setIsModalOpen(true)}
            style={{ borderRadius: 8 }}
          />
        </div>
      </div>

      {viewMode === "table" ? (
        <Card
          style={{
            borderRadius: 12,
            boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
            border: "none",
          }}
          bodyStyle={{ padding: 0 }}
        >
          <CandidateTable
            candidates={filteredCandidates}
            onStatusChange={handleStatusChange}
            onDelete={handleDeleteCandidate}
            onView={(c) => setSelectedCandidate(c)}
          />
        </Card>
      ) : (
        <KanbanBoard
          candidates={filteredCandidates}
          onStatusChange={handleStatusChange}
          onView={(c) => setSelectedCandidate(c)}
        />
      )}
    </>
  );

  let activeKey = "/";
  if (location.pathname.includes("/candidates")) activeKey = "/candidates";
  if (location.pathname.includes("/roles")) activeKey = "/roles";

  const menuItems = [
    { key: "/", label: "Dashboard", icon: <HomeOutlined /> },
    { key: "/candidates", label: "Candidates", icon: <TeamOutlined /> },
    { key: "/roles", label: "Jobs & Roles", icon: <SolutionOutlined /> },
  ];

  const handleMenuClick = (key: string) => {
    navigate(key);
    setMobileMenuOpen(false);
  };

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Header
        style={{
          background: "#fff",
          padding: "0 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          zIndex: 10,
          height: 70,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <div
            className="app-logo"
            style={{
              marginRight: 48,
              cursor: "pointer",
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
            onClick={() => navigate("/")}
          >
            SOFT HR
          </div>

          {/* --- DESKTOP MENU --- */}
          <div className="desktop-menu">
            <Menu
              mode="horizontal"
              selectedKeys={[activeKey]}
              onClick={(e) => handleMenuClick(e.key)}
              items={menuItems}
              style={{
                borderBottom: "none",
                minWidth: 400,
                fontSize: "15px",
                fontWeight: 500,
              }}
            />
          </div>

          {/* --- MOBILE HAMBURGER BUTTON --- */}
          <Button
            className="mobile-menu-btn"
            type="text"
            icon={<MenuOutlined style={{ fontSize: "20px" }} />}
            onClick={() => setMobileMenuOpen(true)}
          />
        </div>
      </Header>

      {/* --- MOBILE DRAWER MENU --- */}
      <Drawer
        title="Menu"
        placement="right"
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        width={250}
        bodyStyle={{ padding: 0 }}
      >
        <Menu
          mode="vertical"
          selectedKeys={[activeKey]}
          onClick={(e) => handleMenuClick(e.key)}
          items={menuItems}
          style={{ borderRight: "none" }}
        />
      </Drawer>

      <Content
        style={{
          padding: "32px 40px",
          maxWidth: 1400,
          margin: "0 auto",
          width: "100%",
        }}
      >
        <Routes>
          <Route
            path="/"
            element={
              <Home
                candidates={candidates}
                roles={roles}
                onViewCandidate={(c) => setSelectedCandidate(c)}
              />
            }
          />
          <Route path="/candidates" element={<CandidateView />} />
          <Route
            path="/roles"
            element={
              <RoleManager
                roles={roles}
                onAddRole={handleAddRole}
                onDeleteRole={handleDeleteRole}
                onReorderRoles={handleReorderRoles}
              />
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Content>

      <Footer style={{ textAlign: "center", color: "#94a3b8" }}>
        SOFT HR © {new Date().getFullYear()} • Designed for Speed
      </Footer>

      <AddCandidateModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={handleAddCandidate}
        roles={roles}
      />

      <CandidateDrawer
        candidate={selectedCandidate}
        isOpen={!!selectedCandidate}
        onClose={() => setSelectedCandidate(null)}
        onAddNote={handleAddNote}
      />
    </Layout>
  );
};

// --- THEME CONFIGURATION ---
const App: React.FC = () => {
  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: "'Work Sans', sans-serif",
          colorPrimary: "#4f46e5", // Indigo 600
          borderRadius: 8,
          colorBgContainer: "#ffffff",
          colorTextHeading: "#1e293b",
        },
        components: {
          Button: {
            fontWeight: 500,
            controlHeight: 40,
            controlHeightLG: 48,
          },
          Card: {
            headerFontSize: 18,
          },
          Table: {
            headerBg: "#f8fafc",
            headerColor: "#64748b",
            rowHoverBg: "#f1f5f9",
          },
        },
      }}
    >
      <BrowserRouter>
        <AppLayout />
      </BrowserRouter>
    </ConfigProvider>
  );
};

export default App;
